import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import type { Product, ProductFamily } from '../types';
import CircularProductCard from './CircularProductCard';

interface ProductShowcaseProps {
  products: Product[];
  bgWhite?: boolean;
}

const FILTERS: { label: string; value: ProductFamily | 'TODO' }[] = [
  { label: 'Todo', value: 'TODO' },
  { label: 'Cabinas', value: 'CABINAS' },
  { label: 'Espejos', value: 'ESPEJOS' },
  { label: 'Video', value: 'VIDEO' },
  { label: 'Experiencias', value: 'ESCENARIOS' },
];

/** Las palabras que rotan en el titular. */
const TITLE_OPTIONS = ['Cabinas de Fotos', 'Espejos Mágicos', 'Cabinas 360', 'Experiencias'];

const ProductShowcase: React.FC<ProductShowcaseProps> = ({ products, bgWhite = false }) => {
  const [filter, setFilter] = useState<ProductFamily | 'TODO'>('TODO');
  const [visibleCount, setVisibleCount] = useState(8);
  /* Vueltas del rotador. La palabra solo entra animada a partir del primer
     cambio: la inicial se publica visible en el HTML (antes framer-motion la
     escribía con opacity:0 y no aparecía hasta que cargaba el JavaScript). */
  const [vueltas, setVueltas] = useState(0);
  const titleIndex = vueltas % TITLE_OPTIONS.length;

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const timer = window.setInterval(
      () => setVueltas((v) => v + 1),
      3000
    );
    return () => window.clearInterval(timer);
  }, []);

  const filtered =
    filter === 'TODO' ? products : products.filter((p) => p.family === filter);
  const visible = filtered.slice(0, visibleCount);
  const hasMore = visible.length < filtered.length;

  return (
    <section
      id="cabinas"
      className={`relative overflow-hidden py-20 ${bgWhite ? 'bg-white' : 'bg-cabina-cream'}`}
    >
      {/* Ornamentos de fondo */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden opacity-20" aria-hidden="true">
        <div className="absolute left-10 top-10 h-32 w-32 animate-pulse rounded-full border border-cabina-red" />
        <div className="absolute bottom-20 right-10 h-64 w-64 animate-float rounded-full border-2 border-cabina-red/30" />
      </div>

      <div className="container relative z-10 mx-auto px-6">
        <div className="mb-16 text-center">
          <span className="eyebrow-cdf mb-3">Descubre</span>
          <h2 className="mb-8 flex flex-col items-center justify-center gap-2 font-sans text-4xl font-bold text-black md:text-6xl">
            <span className="flex flex-col items-center gap-x-4 md:flex-row">
              <span>Nuestras</span>
              <span className="relative flex h-[1.2em] min-w-[280px] items-center justify-center md:min-w-[330px] md:justify-start">
                <span
                  key={vueltas}
                  className={`block whitespace-nowrap font-serif italic text-cabina-red ${
                    vueltas > 0 ? 'animate-word-in' : ''
                  }`}
                >
                  {TITLE_OPTIONS[titleIndex]}
                </span>
              </span>
            </span>
          </h2>

          <div className="flex flex-wrap justify-center gap-2 md:gap-3">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => {
                  setFilter(f.value);
                  setVisibleCount(8);
                }}
                aria-pressed={filter === f.value}
                className={`rounded-full border px-7 py-2.5 text-xs font-bold uppercase tracking-widest transition-all duration-300 md:text-sm ${
                  filter === f.value
                    ? 'scale-105 border-cabina-red bg-cabina-red text-white shadow-xl'
                    : 'border-gray-200 bg-white/50 text-gray-500 backdrop-blur-sm hover:border-black hover:text-black'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-3 gap-y-6 sm:gap-x-8 sm:gap-y-12 md:gap-16 lg:grid-cols-4 lg:gap-x-10">
          {visible.map((product) => (
            <CircularProductCard
              key={product.id}
              product={product}
              variant={product.family === 'ESCENARIOS' ? 'innovation' : 'default'}
            />
          ))}
        </div>

        {hasMore && (
          <div className="mt-16 text-center">
            <button
              onClick={() => setVisibleCount((v) => v + 4)}
              className="group inline-flex items-center gap-3 rounded-full border-2 border-cabina-red/10 bg-white px-10 py-4 text-xs font-bold uppercase tracking-widest text-gray-700 shadow-lg transition-all hover:border-cabina-red hover:bg-cabina-red hover:text-white hover:shadow-cabina-red/20"
            >
              Ver más opciones
              <ChevronDown size={14} className="transition-transform group-hover:translate-y-1" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductShowcase;
